import {
	ApiError,
	type MeStore,
	type StoreRoleTemplate,
	type StoreUser,
} from "@khazzin/api-client";
import type { AccessLevel } from "@khazzin/api-client/permissions";
import { SECTIONS, type SectionKey } from "../../app/shell/sections";

/**
 * Team & Access as the portal draws it (EP10-S10).
 *
 * **None of this decides anything.** The server runs the ceiling, the finance
 * rule, the last owner and the limit on every write; these only choose what to
 * disable, what to label and which row to paint when a refusal comes back.
 */

export type Grid = Record<SectionKey, AccessLevel>;

/** The five templates a sub-account may take; the owner's is never offered. */
export const SUB_ACCOUNT_TEMPLATES: readonly StoreRoleTemplate[] = [
	"store_manager",
	"store_staff",
	"store_finance",
	"store_customer_service",
	"store_viewer",
];

/** A full thirteen-cell grid from whatever the server sent; absent is none. */
export function gridFrom(access: Readonly<Record<string, AccessLevel>> | undefined): Grid {
	const out = {} as Grid;
	for (const section of SECTIONS) {
		const level = access?.[section];
		out[section] = level === "read" || level === "write" ? level : "none";
	}
	return out;
}

export function sameGrid(a: Grid, b: Grid): boolean {
	return SECTIONS.every((section) => a[section] === b[section]);
}

/**
 * The editor's ceiling: their own grid on this store, with `team` capped at
 * read for anybody but the owner — handing out `team = write` is the owner's
 * alone (`01/06` §3.2).
 */
export function ceilingOf(store: MeStore | null): Grid {
	const own = gridFrom(store?.access);
	return {
		...own,
		team: store?.accountKind !== "owner" && own.team === "write" ? "read" : own.team,
	};
}

/** Seats the limit counts: sub-accounts that are not revoked. */
export function seatsUsed(members: readonly StoreUser[]): number {
	return members.filter((m) => m.accountKind === "sub_account" && m.status !== "revoked").length;
}

/**
 * Whether turning finance on will meet the second-factor rule: only a change
 * asks, and an account the mirror says is enrolled passes.
 */
export function needsMfa(grid: Grid, saved: Grid | null, member?: StoreUser): boolean {
	return grid.finance === "write" && saved?.finance !== "write" && member?.mfaEnrolled !== true;
}

/** The grid row a refusal names (`access.<section>`), when it names one. */
export function refusedSection(error: unknown): SectionKey | null {
	if (!(error instanceof ApiError) || error.field === null || !error.field.startsWith("access.")) {
		return null;
	}
	const section = error.field.slice("access.".length);
	return (SECTIONS as readonly string[]).includes(section) ? (section as SectionKey) : null;
}

export type MemberAction =
	| "edit"
	| "resetPassword"
	| "endSessions"
	| "suspend"
	| "reactivate"
	| "revoke"
	| "activity"
	| "transfer";

/**
 * A member row's menu, in order, each with whether it is available. The
 * owner's destructive actions are drawn disabled rather than hidden — "the last
 * owner cannot be removed" is information. Transfer is offered only to the
 * owner, and only towards an active account.
 */
export function memberActions(
	member: StoreUser,
	callerIsOwner: boolean,
): { action: MemberAction; enabled: boolean }[] {
	const revoked = member.status === "revoked";
	const owner = member.accountKind === "owner";
	const out: { action: MemberAction; enabled: boolean }[] = [
		{ action: "edit", enabled: !revoked && !owner },
		{ action: "resetPassword", enabled: !revoked && member.status !== "suspended" },
		{ action: "endSessions", enabled: !revoked && member.userId !== "" },
		member.status === "suspended"
			? { action: "reactivate", enabled: true }
			: { action: "suspend", enabled: !revoked && !owner },
		{ action: "revoke", enabled: !revoked && !owner },
		{ action: "activity", enabled: true },
	];
	if (callerIsOwner && !owner) {
		out.push({ action: "transfer", enabled: member.status === "active" });
	}
	return out;
}

/**
 * The link an invitation's token opens, on this portal. The token rides in the
 * fragment, which a browser never sends to a server or writes to a log.
 */
export function acceptLink(token: string, origin = window.location.origin): string {
	return `${origin}/invitations/accept#token=${encodeURIComponent(token)}`;
}

/** Where a token waits while its holder signs in; the fragment does not survive that trip. */
const PENDING_INVITE_KEY = "khazzin.invite.token";

/**
 * Takes the token from `#token=` into session storage and strips it from the
 * address, so it is neither left in history nor lost across sign-in. Returns
 * the token waiting, if any.
 */
export function captureInviteToken(): string | null {
	const match = /(?:^#|&)token=([^&]+)/.exec(window.location.hash);
	try {
		if (match?.[1] !== undefined) {
			window.sessionStorage.setItem(PENDING_INVITE_KEY, decodeURIComponent(match[1]));
			window.history.replaceState(null, "", window.location.pathname);
		}
		return window.sessionStorage.getItem(PENDING_INVITE_KEY);
	} catch {
		// Storage denied: the token in the address is all there is.
		return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
	}
}

/** Whether an invitation is waiting to be redeemed in this tab. */
export function pendingInvite(): boolean {
	try {
		return window.sessionStorage.getItem(PENDING_INVITE_KEY) !== null;
	} catch {
		return false;
	}
}

export function clearInviteToken() {
	try {
		window.sessionStorage.removeItem(PENDING_INVITE_KEY);
	} catch {
		// Nothing was stored.
	}
}
